import React, { memo } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Stack,
  IconButton,
  Tooltip,
  Divider,
  Chip,
} from "@mui/material";
import { CalendarDays } from "lucide-react";
import ShareIcon from "@mui/icons-material/Share";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import MUITypography from "../../Components/MUITypography";
import { align_center, align_row } from "../../Styles/VMStyles";
import { ROUTE_FEEDBACK } from "../../Util/VMConstants";

const FeedbackRemainders = ({ data = [] }) => {
  const navigate = useNavigate();
  // 🔹 Format date helper
  const formatDate = (dateString) => {
    if (!dateString) return "—";
    const d = new Date(dateString);
    return d.toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  // 🔹 Share handler
  const handleShare = (row) => {
    const message = `
📋 Feedback Request from Venue Manager Application

Hello ${row.recipient_name},

We value your presence at our venue and would love to hear your thoughts. 
Please take a moment to share your feedback using the link below:

${row.feedback_link}

Your insights help us improve our services. 
Thank you for your time and support! 🙏
`;

    if (navigator.share) {
      navigator
        .share({
          title: "Feedback Request - Venue Manager",
          text: message,
          url: row.feedback_link,
        })
        .catch((err) => console.error("Share failed:", err));
    } else {
      navigator.clipboard.writeText(message).then(() => {
        toast.success("Feedback link copied with message!");
      });
    }
  };

  // if empty, show placeholder
  if (!Array.isArray(data) || data.length === 0) {
    return (
      <Card variant="outlined" sx={{ borderRadius: 3 ,minHeight: '180px',minWidth:'250px'}}>

      <MUITypography variant="h4" gutterBottom sx={{fontWeight:700,p:2}}>
          Feedback Reminders
        </MUITypography>
        
        <CardContent sx={{ textAlign: "center"}}>
            <Box sx= {{...align_center,...align_row,width:'100%',height:'100%',flexDirection:'column',gap:2 }}>
              
                           <Box
                                        component="img"
                                        src={'/static/feedback.svg'}
                                        alt="Feedback"
                                        sx={{
                                          mt:5,
                                          height: { xs: 50, md: 50 },
                                        }}
                                      />
                        <Typography variant="body2" color="text.secondary" sx={{ flexGrow: 1,mb:1 }}>
                          No Pending Feedbacks.
                        </Typography>
                     
                        </Box>   


        </CardContent>
      </Card>
    );
  }

  const total = data[0]?.total_pending_feedback_count ?? 0;

  return (
    <Card variant="outlined" sx={{ borderRadius: 3 ,minHeight: '200px',minWidth:'250px'}}>
      <CardContent>
        {/* Header */}
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={2}
        >
          
        <MUITypography variant="h4" gutterBottom sx={{fontWeight:700}}>
          Feedback Reminders
        </MUITypography>
       
          <Chip
            label={`Total Pending: ${total}`}
            color="error"
            variant="outlined"
            size="small"
          />
        </Box>

        <Divider sx={{ mb: 2 }} />

        {/* Feedback List */}
        <Stack spacing={1.5}>
          {data.map((item, i) => {
            const name = item.recipient_name ?? "Unknown";

            return (
              <Box
                key={item.id ?? i}
                onClick={() => navigate("/" + ROUTE_FEEDBACK)}
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  p: 1.5,
                  borderRadius: 2,
                  bgcolor: i % 2 === 0 ? "#F9FBFF" : "white",
                  border: "1px solid #eee",
                  cursor: "pointer",
                  "&:hover": {
                    bgcolor: "action.hover",
                  },
                }}
              >
                <Box display="flex" alignItems="center" gap={2}>
                  <CalendarDays size={20} color="#1E3A8A" />
                  <Box>
                    <Typography fontWeight={500}>{name}</Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      fontSize={12}
                    >
                      {formatDate(item.created_date)}
                    </Typography>
                  </Box>
                </Box>

                {/* Share Action */}
                <Tooltip title="Share Feedback Form">
                  <IconButton
                    color="primary"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleShare(item);
                    }}
                    aria-label={`share-${item.id ?? name}`}
                  >
                    <ShareIcon />
                  </IconButton>
                </Tooltip>
              </Box>
            );
          })}
        </Stack>
      </CardContent>
    </Card>
  );
};

FeedbackRemainders.propTypes = {
  data: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      recipient_name: PropTypes.string,
      feedback_link: PropTypes.string,
      created_date: PropTypes.string,
      total_pending_feedback_count: PropTypes.number,
    })
  ),
};

export default memo(FeedbackRemainders);
